import { INFO_CATEGORIES, InfoCategoryId } from "./model";

/**
 * Which backend label family each dashboard category is — one mapping, keyed
 * by L0 label id, for everything that needs a category: the cards' counts,
 * the share lines' pool, the drill-downs' filters and the breakdowns
 * (frontend fix list F3, Oct 2026).
 *
 * Before this, the counts read Personal from the "Other" family while the
 * pool and the drill-downs read it from the Personal Information family, so
 * a percentage divided one population by another.
 *
 *   row          family (L0 code)
 *   Personal     Other (1)                 — as agreed; see below
 *   Governance   Governance (2)
 *   Business     Business (3)
 *   Financial    Financial (4)
 *   HR           HR (5)
 *   Legal        Legal (6)
 *   —            Personal Information (7)  — the PII / Regulated family
 *
 * The Personal row is the Other family, per the integration notes. The
 * Personal Information family is a different one that only sounds alike: it
 * is recorded here as `pii` and belongs to no row, so it cannot stand in for
 * Other or slip into a denominator. Giving it a row of its own, or redefining
 * Personal, is a product decision — change this table when it is made.
 */
export type CategoryFamily = InfoCategoryId | "pii";

/** the app's fixed top-level labels, by `code` (common/icons.map L0_ICONS) */
const FAMILY_BY_L0_CODE: Record<number, CategoryFamily> = {
  1: "personal",
  2: "governance",
  3: "business",
  4: "financial",
  5: "hr",
  6: "legal",
  7: "pii",
};

const normalise = (s: string) => s.toLowerCase().replace(/[^a-z]/g, "");

/** only for a label whose code is not one of the seven — a tenant's own L0 */
const FAMILY_BY_NAME: Record<string, CategoryFamily> = {
  other: "personal",
  personal: "pii",
  personalinformation: "pii",
  regulated: "pii",
  hr: "hr",
  humanresources: "hr",
  legal: "legal",
  governance: "governance",
  business: "business",
  financial: "financial",
  finance: "financial",
  financialinformation: "financial",
};

/** what an L0 label carries that the mapping reads */
export interface L0Label {
  id: string;
  code?: number;
  shortName?: string;
  labelName?: string;
  name?: string;
}

export interface CategoryFamilies {
  /** each row's L0 label ids — what its queries and lists filter on */
  l0s_of: Record<InfoCategoryId, string[]>;
  /** the row an L0 label id belongs to; the PII family is not in here */
  category_of: Map<string, InfoCategoryId>;
  /** the Personal Information (PII / Regulated) family, kept apart */
  pii_l0s: string[];
  /** L0 labels in no family, by name, for the load's warning */
  unmatched: string[];
}

const family_of = (l0: L0Label): CategoryFamily | undefined =>
  (l0.code !== undefined && FAMILY_BY_L0_CODE[l0.code]) ||
  [l0.shortName, l0.labelName, l0.name]
    .map((n) => FAMILY_BY_NAME[normalise(n || "")])
    .find(Boolean);

export const build_category_families = (l0s: readonly L0Label[]): CategoryFamilies => {
  const families: CategoryFamilies = {
    l0s_of: INFO_CATEGORIES.reduce(
      (acc, c) => ({ ...acc, [c.id]: [] }),
      {} as Record<InfoCategoryId, string[]>
    ),
    category_of: new Map(),
    pii_l0s: [],
    unmatched: [],
  };
  l0s.forEach((l0) => {
    if (!l0 || !l0.id) return;
    const family = family_of(l0);
    if (!family) families.unmatched.push(l0.labelName || l0.name || l0.id);
    else if (family === "pii") families.pii_l0s.push(l0.id);
    else {
      families.l0s_of[family].push(l0.id);
      families.category_of.set(l0.id, family);
    }
  });
  return families;
};

/** every L0 label id behind the six rows, in row order */
export const family_label_ids = (families: CategoryFamilies): string[] =>
  INFO_CATEGORIES.reduce((ids, c) => [...ids, ...families.l0s_of[c.id]], [] as string[]);
